import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { debugLog } from "../lib/debug";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capture ?ref=CODE from any landing URL and persist for later signup
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref) localStorage.setItem("cf_ref", ref.toUpperCase());
    } catch (e) {
      debugLog("ref-capture skipped:", e?.message);
    }

    // Skip /me check if we're processing OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Phase 12: prefer httpOnly cookie. Render cached user immediately for UX,
    // then hit /auth/me to refresh.
    const cached = localStorage.getItem("cf_user");
    if (cached) {
      try { setUser(JSON.parse(cached)); } catch (e) { debugLog("bad cached user", e?.message); }
    }
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      localStorage.setItem("cf_user", JSON.stringify(r.data));
    }).catch(() => {
      localStorage.removeItem("cf_user");
      setUser(null);
    }).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Phase 12: token lives in httpOnly cookie set by the backend.
    localStorage.setItem("cf_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password, ref_code = null) => {
    const payload = { name, email, password };
    if (ref_code) payload.ref_code = ref_code;
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("cf_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { debugLog("logout call failed (already signed out?):", e?.message); }
    localStorage.removeItem("cf_user");
    setUser(null);
  };

  const refreshUser = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data);
    localStorage.setItem("cf_user", JSON.stringify(r.data));
  };

  const setUserDirect = (u) => {
    setUser(u);
    if (u) localStorage.setItem("cf_user", JSON.stringify(u));
  };

  /** Optimistic local credit update after an AI action returns _credits_remaining. */
  const setCredits = (remaining) => {
    if (typeof remaining !== "number") return;
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, credits: remaining };
      localStorage.setItem("cf_user", JSON.stringify(next));
      return next;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUserDirect, setCredits }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
